import React, { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

const DEFAULT_MODIFIERS = [0, 0, 0, 0, 10, 15, -5]

export default function DayOfWeekChart() {
  const [baseRate, setBaseRate] = useState(100)
  const [modifiers, setModifiers] = useState<number[]>(DEFAULT_MODIFIERS)

  const rates = useMemo(
    () => modifiers.map((mod) => Math.round(baseRate * (1 + mod / 100))),
    [baseRate, modifiers]
  )

  const maxAbsMod = useMemo(() => {
    const abs = modifiers.map((m) => Math.abs(m))
    return Math.max(...abs, 1)
  }, [modifiers])

  const handleModifier = (index: number, value: string) => {
    const num = value === '' || value === '-' ? 0 : parseFloat(value)
    const next = [...modifiers]
    next[index] = isNaN(num) ? 0 : Math.max(-100, Math.min(100, num))
    setModifiers(next)
  }

  const chartHeight = 160
  const zeroY = chartHeight / 2

  return (
    <div className="space-y-4">
      {/* Base rate input */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-foreground whitespace-nowrap">
          Base rate
        </label>
        <div className="relative w-28">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            £
          </span>
          <Input
            type="number"
            min={10}
            max={500}
            value={baseRate}
            onChange={(e) => setBaseRate(Math.max(1, parseInt(e.target.value) || 1))}
            className="h-8 pl-7 text-sm tabular-nums"
          />
        </div>
      </div>

      {/* Bar chart */}
      <div className="flex items-end justify-between gap-2 px-1">
        {DAYS.map((day, i) => {
          const mod = modifiers[i]
          const rate = rates[i]
          const isPositive = mod > 0
          const isNegative = mod < 0
          const isZero = mod === 0

          // Bar height proportional to modifier magnitude
          const barMaxHeight = zeroY - 8
          const barHeight = Math.max(2, (Math.abs(mod) / maxAbsMod) * barMaxHeight)

          return (
            <div key={day} className="flex flex-1 flex-col items-center gap-1.5">
              {/* Rate label */}
              <span
                className={cn(
                  'text-xs font-semibold tabular-nums transition-colors duration-200',
                  isPositive && 'text-emerald-600',
                  isNegative && 'text-rose-600',
                  isZero && 'text-slate-500'
                )}
              >
                £{rate}
              </span>

              {/* Bar area */}
              <div
                className="relative flex w-full items-center justify-center"
                style={{ height: chartHeight }}
              >
                {/* Zero line */}
                <div
                  className="absolute left-0 right-0 border-t border-dashed border-slate-300"
                  style={{ top: zeroY }}
                />

                {/* Bar */}
                <div
                  className={cn(
                    'absolute left-1 right-1 rounded-sm transition-all duration-300 ease-out',
                    isPositive && 'bg-emerald-400/80',
                    isNegative && 'bg-rose-400/80',
                    isZero && 'bg-slate-300/60'
                  )}
                  style={
                    isNegative
                      ? { top: zeroY + 1, height: barHeight }
                      : { bottom: chartHeight - zeroY + 1, height: isZero ? 2 : barHeight }
                  }
                />

                {/* Modifier percentage on bar */}
                {!isZero && (
                  <span
                    className={cn(
                      'absolute text-[10px] font-medium tabular-nums transition-all duration-300',
                      isPositive && 'text-emerald-700',
                      isNegative && 'text-rose-700'
                    )}
                    style={
                      isNegative
                        ? { top: zeroY + barHeight + 4 }
                        : { bottom: chartHeight - zeroY + barHeight + 4 }
                    }
                  >
                    {mod > 0 ? '+' : ''}
                    {mod}%
                  </span>
                )}
              </div>

              {/* Day label */}
              <span className="text-xs font-medium text-muted-foreground">{day}</span>

              {/* Modifier input */}
              <div className="relative w-full">
                <Input
                  type="number"
                  value={modifiers[i]}
                  onChange={(e) => handleModifier(i, e.target.value)}
                  className="h-7 w-full px-1 text-center text-xs tabular-nums"
                  min={-100}
                  max={100}
                />
                <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                  %
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
